import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { fromJS } from "immutable";
import GridView from "./GridView";
import ListView from "./ListView";
import Loading from "./Loading";
import TaskAction from "../actions/TaskAction";
import { useCurrentSize } from "../CurrentSize";
import { useQuery } from "../AppLayout";
import BoardComponent from "../board/BoardComponent";
import SearchFilter from "./component/SearchFilter";
import {
  downloadBlob,
  exportData,
  parseExportErrorMessage,
  type ExportFormat,
} from "../api/DataApi";
import MapView from "../map/MapView";
import { AppContext } from "../Contexts";
import SelectionItemsDialog from "../dialog/SelectionItemsDialog";
import ExportCSVDialog from "../dialog/ExportCSVDialog";
import LayoutAction from "../actions/LayoutAction";
import CalendarView from "../calendar/CalendarView";
import InformationInlineDialog from "../dialog/InformationInlineDialog";
import { ContentTypes } from "../constants/action-types";
import EmailDialog from "../dialog/EmailDialog";

export const getFilterActivity = (appView: string, currentUser: any) => {
  let filterByView = {};
  if (appView === "Tasks") {
    filterByView = {
      activityType: { operator: "is", dataType: "string", values: ["task"] },
    };
  } else if (appView === "Notes") {
    filterByView = {
      activityType: { operator: "is", dataType: "string", values: ["commented"] },
    };
  } else if (appView === "Drafts") {
    filterByView = {
      assignees: {
        operator: "is",
        dataType: "string",
        values: [currentUser.get("accountId")],
      },
      emailStatus: {
        operator: "is",
        dataType: "string",
        values: ["DRAFT"],
      },
      activityType: {
        operator: "is",
        dataType: "string",
        values: ["sendDraftEmail"],
      },
    };
  } else if (appView === "Outbox") {
    filterByView = {
      assignees: {
        operator: "is",
        dataType: "string",
        values: [currentUser.get("accountId")],
      },
      emailStatus: {
        operator: "is",
        dataType: "string",
        values: ["SCHEDULED"],
      },
      activityType: {
        operator: "is",
        dataType: "string",
        values: ["sendScheduledEmail"],
      },
    };
  } else if (appView === "Tracked") {
    filterByView = {
      assignees: {
        operator: "is",
        dataType: "string",
        values: [currentUser.get("accountId")],
      },
      emailStatus: {
        operator: "is_not",
        dataType: "string",
        values: ["DRAFT"],
      },
      tracking: {
        operator: "is",
        dataType: "bool",
        values: [true],
      },
      activityType: {
        operator: "in",
        dataType: "string",
        values: ["sendEmail", "sendDraftEmail", "recordSentEmail"],
      },
    };
  }
  return filterByView;
};

const Workspace = () => {
  const currentSize = useCurrentSize();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();
  const currentParams = useQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const errorMessage = useSelector((state: any) =>
    state.projectReducer.getIn(["error", "message"])
  );

  const pending = useSelector((state: any) => state.taskReducer.get("pending"));
  const pagination = useSelector((state: any) => state.taskReducer.getIn(["pagination"]));
  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const sortFields = useSelector((state: any) => state.taskReducer.getIn(["sorts"]));

  const projectViewId = currentParams.get("id") || "";
  const projectView = currentParams.get("view") || "GRID";
  const contentType = currentParams.get("type") || "SALE";
  const appView = currentParams.get("key") || "";

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const myFilters = useSelector((state: any) => state.filterReducer.get("myFilters")) || fromJS([]);
  const myFiltersByType = myFilters.filter((item) =>
    (item.getIn(["data", "types"])?.toJS() || []).includes(contentType)
  );
  const currentViewData = projectViewId
    ? myFiltersByType.find((item) => item.get("id") === projectViewId)
    : myFiltersByType.find(
        (item) => !item.get("id") && item.getIn(["data", "view"]) === projectView
      );
  const currentViewName = currentViewData?.get("name") || projectView;

  const gridData = useSelector((state: any) => state.taskReducer.getIn([projectView]));
  const items =
    useSelector((state: any) => state.taskReducer.getIn([projectView, "children"])) || fromJS([]);
  const [size, setSize] = useState(currentSize);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const appContext = useContext<any>(AppContext);

  useEffect(() => {
    document.title = projectViewId ? currentViewName : appView;
  }, [projectViewId, currentViewName, appView]);

  useEffect(() => {
    // 150px accounts for Gmail top header (~64px) + Action toolbar (~48px) + InboxSDK paddings (~38px)
    const minusHeight = appContext.appName == "chrome" ? 150 : 0;
    const minusWidth = appContext.appName == "chrome" ? 56 : 0;
    setSize({
      width: currentSize.width - minusWidth,
      height: currentSize.height - minusHeight,
    });
  }, [currentSize, appContext]);

  // If user lands on /workspaces/:projectId with key=Apps in query (OAuth callback),
  // redirect to the proper apps route so AppList can handle the callback.
  useEffect(() => {
    const key = appView.toLowerCase();
    const isApps = key === "apps";
    const isAlreadyAppsPath = /\/apps\/?$/.test(location.pathname);
    if (projectId && isApps && !isAlreadyAppsPath) {
      // Preserve query string but move to the proper /apps route so AppList can handle OAuth callback
      navigate(`/workspaces/${projectId}/settings/apps${location.search}`, {
        replace: true,
      });
    }
  }, [projectId, location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchTasks = ({
    projectId,
    page = pagination.getIn(["number"]),
    pageSize = pagination.getIn(["size"]),
    filters = taskFilters.toJS(),
    sorts = sortFields.toJS(),
    types = [contentType],
  }: any) => {
    if (page >= 0 && projectId && contentType) {
      let filterByView = {};
      if (contentType === ContentTypes.ACTIVITY) {
        filterByView = getFilterActivity(appView, currentUser);
      }
      const searchRequest = {
        projectId,
        page,
        pageSize,
        filters: {
          ...filters,
          ...filterByView,
        },
        sorts,
        view: projectView,
        types,
      };
      dispatch(TaskAction.searchItems(searchRequest));
    }
  };

  const renderView = () => {
    switch (projectView) {
      case "GRID":
        if (
          contentType === ContentTypes.ACTIVITY &&
          ["Tasks", "Notes", "Drafts", "Outbox", "Tracked"].includes(appView)
        ) {
          return <ListView items={items} loading={pending} onSearchTasks={handleSearchTasks} />;
        }
        return (
          <GridView
            gridData={gridData}
            currentSize={size}
            loading={pending}
            onChangeFilter={(filters) => {
              onChangeFilterAndSort(filters, sortFields.toJS());
            }}
            onSearchTasks={handleSearchTasks}
          />
        );
      case "CALENDAR":
        return (
          <CalendarView
            projectId={projectId}
            currentSize={size}
            onSearchTasks={handleSearchTasks}
          />
        );

      case "MAP":
        return <MapView currentSize={size} />;

      case "BOARD":
        return (
          <BoardComponent
            currentSize={size}
            contentType={contentType}
            projectView={projectView}
            onSearchTasks={handleSearchTasks}
            loading={pending}
          />
        );
    }
    return <Loading />;
  };

  const onChangeFilterAndSort = (filters: any, sorts: any[]) => {
    const page = 0;
    const pageSize = pagination.getIn(["size"]);
    handleSearchTasks({
      projectId,
      page,
      pageSize,
      filters,
      sorts,
    });
    dispatch(TaskAction.changeFilters(filters));
    dispatch(TaskAction.changeSorts(sorts));
  };

  const handleOnExport = () => {
    setShowExportDialog(true);
  };

  const handleExportWithColumns = (selectedColumns: string[], exportFormat: ExportFormat) => {
    setExporting(true);
    const exportRequest = {
      filters: taskFilters.toJS(),
      types: [contentType],
      columns: selectedColumns,
    };
    const contentTypeLabels: Record<string, string> = {
      SALE: "Deals",
      CONTACT: "Contacts",
      COMPANY: "Companies",
    };
    const typeLabel = contentTypeLabels[contentType] || contentType;
    const safeViewName =
      String(currentViewName || "")
        .replace(/[/\\:*?"<>|]/g, "_")
        .trim() || "Export";
    const dateStr = format(new Date(), "yyyy-MM-dd");
    const ext = exportFormat === "xlsx" ? "xlsx" : "csv";
    const filename = `${typeLabel} - ${safeViewName} ${dateStr}.${ext}`;
    exportData(projectId, exportFormat, exportRequest)
      .then((blob) => {
        downloadBlob(blob, filename);
        dispatch(
          LayoutAction.setNotification({
            title: "Export",
            type: "success",
            message: "Exported successfully",
          })
        );
        setShowExportDialog(false);
      })
      .catch(async (error) => {
        const message = await parseExportErrorMessage(error);
        dispatch(
          LayoutAction.setNotification({
            title: "Export",
            type: "error",
            message,
          })
        );
      })
      .finally(() => {
        setExporting(false);
      });
  };

  if (errorMessage == "NOT_FOUND") {
    navigate("/workspaces");
  }

  return (
    <div
      className="w-full flex flex-col"
      style={{ height: appContext.appName === "chrome" ? size.height : "100%" }}
    >
      <SearchFilter
        projectViewId={projectViewId}
        projectView={projectView}
        appView={appView}
        contentType={contentType}
        onChangeFilterAndSort={onChangeFilterAndSort}
        onExportCSV={handleOnExport}
        pagination={pagination}
        projectId={projectId}
        items={items}
        currency={project.get("currency")}
        goToPage={({ page = 0, pageSize = pagination.get("size") }) => {
          const projectId = project.get("id");
          handleSearchTasks({ projectId, page, pageSize });
        }}
      />
      <div className="flex-1 min-h-0 overflow-hidden" style={{ maxWidth: size.width - 60 }}>
        {renderView()}
      </div>
      <EmailDialog onSearchTasks={handleSearchTasks} />
      <InformationInlineDialog />
      <SelectionItemsDialog
        onBulkDelete={() => {
          handleSearchTasks({ projectId: project.get("id") });
        }}
      />
      <ExportCSVDialog
        visible={showExportDialog}
        contentType={contentType}
        onClose={() => setShowExportDialog(false)}
        onExport={handleExportWithColumns}
        exporting={exporting}
      />
    </div>
  );
};

export default Workspace;
